import { FlashList } from '@shopify/flash-list';
import * as React from 'react';
import { ScrollView, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { JobTimesheetData } from './types';
import { Small } from '../ui/typography';
import { FlagIcon } from 'lucide-react-native';
import { useRouter } from 'expo-router';

// fix style

// Index 3 is the founders bonus column: "Founders(£)" is a single unbreakable token, so it
// needs enough room to sit on one line. The rest fall back to an even share of the screen.
const MIN_COLUMN_WIDTHS = [110, 10, 10, 100, 10, 5];

interface WorkedTableProps {
  timesheetData: JobTimesheetData;
}
export default function WorkedTable({ timesheetData }: WorkedTableProps) {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();

  const columnWidths = React.useMemo(() => {
    return MIN_COLUMN_WIDTHS.map((minWidth) => {
      const evenWidth = width / MIN_COLUMN_WIDTHS.length;
      return evenWidth > minWidth ? evenWidth : minWidth;
    });
  }, [width]);

  // Founders bonus (stored as crew_bonus type 'Experience Bonus') is only paid to a
  // handful of crew, so the column is hidden entirely when there is nothing to show.
  const showFoundersBonus = React.useMemo(
    () => timesheetData.rows.some((row) => Number(row.rowXpBonus || 0) !== 0),
    [timesheetData.rows]
  );

  return (
    <ScrollView
      horizontal
      bounces={false}
      showsHorizontalScrollIndicator={false}
      scrollEventThrottle={16}
      style={{ flex: 1 }}
      contentContainerStyle={{ flexGrow: 1 }}
      nestedScrollEnabled={true}
    >
      <Table aria-labelledby='invoice-table'>
        <TableHeader>
          <TableRow>
            <TableHead className='px-0.5' style={{ width: columnWidths[0] }}>
              <Small>Date</Small>
            </TableHead>
            <TableHead style={{ width: columnWidths[1] }}>
              <Small>Time</Small>
            </TableHead>
            <TableHead style={{ width: columnWidths[2] }}>
              <Small>Hours (Extra)</Small>
            </TableHead>
            {showFoundersBonus ? (
              <TableHead className='px-1' style={{ width: columnWidths[3] }}>
                <Small numberOfLines={1}>Founders(£)</Small>
              </TableHead>
            ) : null}
            <TableHead style={{ width: columnWidths[4] }}>
              <Small>Misc(£)</Small>
            </TableHead>
            <TableHead style={{ width: columnWidths[5] }}>
              <Small>Total(£)</Small>
            </TableHead>
            <TableHead style={{ width: columnWidths[6] }}>
              <Small>Issue</Small>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!timesheetData.rows.length && (
            <TableRow>
              <TableCell>
                <Small className='text-gray-500'>No worked hours</Small>
              </TableCell>
            </TableRow>
          )}
          <FlashList
            data={timesheetData.rows}
            scrollEnabled={false}
            contentContainerStyle={{
              paddingBottom: insets.bottom,
            }}
            showsVerticalScrollIndicator={false}
            renderItem={({ item: timesheet, index }) => {
              return (
                <TableRow
                  key={index}
                >
                  <TableCell style={{ width: columnWidths[0] }}>
                    <Small>{timesheet.date}</Small>
                    {timesheet.cancelChargeNote && (
                      <Small className='text-red-500'>{timesheet.cancelChargeNote}</Small>
                    )}
                    {/* Say why the figure is lower. Without this a demoted shift just pays
                        less with no explanation on the one screen the crew member actually
                        looks at, and the first they know is a short payslip. */}
                    {timesheet.demotedPayLevel && (
                      <Small className='text-amber-600'>Paid as {timesheet.demotedPayLevel}</Small>
                    )}
                  </TableCell>
                  <TableCell style={{ width: timesheet.type ? 200 : columnWidths[1] }}>
                    {timesheet.time ? (
                      <Small>
                        {timesheet.time}
                      </Small>
                    ) : (
                      <Small className={
                        timesheet.type === 'addition' ? 'text-green-500' :
                          timesheet.type === 'plInsurance' ? 'text-purple-500' :
                            'text-red-500'
                      }>
                        {timesheet.type !== 'strike' ?
                          timesheet.type === 'addition' ? 'Addition: ' :
                            timesheet.type === 'plInsurance' ? 'PL Insurance' :
                              'Deduction: '
                          : null}
                        {timesheet.additionDeductionNote}
                      </Small>
                    )}
                  </TableCell>
                  <TableCell style={{ width: timesheet.type ? 0 : columnWidths[2] }}>
                    <Small>{timesheet.hours}
                      {timesheet.crew_hours ? <Small className='text-green-500'>{`(${timesheet.crew_hours})`}</Small> : null}
                      {timesheet.crew_adjusted_hours ? <Small className='text-red-500'>{`(${timesheet.crew_adjusted_hours})`}</Small> : null}
                    </Small>
                  </TableCell>
                  {showFoundersBonus ? (
                    <TableCell style={{ width: timesheet.type ? 0 : columnWidths[3] }}>
                      <Small>{timesheet.rowXpBonus?.toFixed(2)}</Small>
                    </TableCell>
                  ) : null}
                  <TableCell style={{ width: timesheet.type ? 0 : columnWidths[4] }}>
                    <Small>{timesheet.misc?.toFixed(2)}</Small>
                  </TableCell>
                  <TableCell style={{ width: columnWidths[5] }}>
                    <Small>{timesheet.rowTotal.toFixed(2)}</Small>
                  </TableCell>
                  <TableCell style={{ width: columnWidths[6] }}>
                    <FlagIcon color={timesheet.timesheetIssueId ? 'red' : 'gray'} size={16} onPress={() => router.push({ pathname: '/timesheets/report', params: { jobCrewId: timesheet.jobCrewId, id: timesheet.timesheetIssueId } })} />
                  </TableCell>
                </TableRow>
              );
            }}
            ListFooterComponent={() => {
              return (
                <>
                  <TableFooter className='mb-1'>
                    <TableRow>
                      <TableCell className='flex-1 justify-center'>
                        <Small className='text-foreground'>Gross total</Small>
                      </TableCell>
                      <TableCell className='items-end pr-20'>
                        <Small>£{timesheetData.total.toFixed(2)}</Small>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='flex-1 justify-center'>
                        <Small className='text-foreground'>Non-Taxable Amount (Fuel & non-taxable skills)</Small>
                      </TableCell>
                      <TableCell className='items-end pr-20'>
                        <Small>£{timesheetData.nonTaxableAmount?.toFixed(2)}</Small>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='flex-1 justify-center'>
                        <Small className='text-foreground'>Taxable Total</Small>
                      </TableCell>
                      <TableCell className='items-end pr-20'>
                        <Small>£{timesheetData.taxableAmount?.toFixed(2)}</Small>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </>
              );
            }}
          />
        </TableBody>
      </Table>
    </ScrollView>
  );
}